<template>
  <div class="activity-page-container">
    <v-dialog v-model="loading" contained max-width="400" persistent class="custom-dialog">
      <v-card rounded="lg">
        <template #text>
          <div class="text-center pt-4">
            <v-progress-circular
              indeterminate
              color="primary"
              size="x-large"
              class="mb-4"
            />

            <div class="font-weight-bold mb-2">
              {{ $t('loadingActivity') }}
            </div>

            <v-progress-linear
              :model-value="progressValue"
              color="primary"
              height="2"
              class="mb-4"
              stream
            />
          </div>
        </template>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col>
        <Datepicker
          v-model="dateRange"
          range="true"
          label="Select range"
          variant="solo"
          :format="getDateFormat(locale)"
          @update:model-value="handleDateRangeChange"
          @cleared="handleDateRangeCleared"
          :dark="themeMode"
          :min-date="minDate"
          :max-date="maxDate"
          clearable
        ></Datepicker>
      </v-col>
    </v-row>

    <!-- Warning Alert -->
    <v-row v-if="showNoActivityWarning">
      <v-col>
        <v-alert
          type="warning"
          variant="tonal"
          border="start"
          closable
        >
          {{ $t('noActivityWarning', { 
            startDate: formatDateForDisplay(dateRange[0]), 
            endDate: formatDateForDisplay(dateRange[1]) 
          }) }}
        </v-alert>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12" md="4">
        <StatusHistoryCategoryStackedBarChart    
          @chartClick="handleChartClick"
          :filterContext="filterStatusHistoryContext"
          :dataLoaded="dataLoaded"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col> 
      <v-col cols="12" md="4">
        <StatusHistoryTeamsStackedBarChart
          @chartClick="handleChartClick"
          :filterContext="filterStatusHistoryContext"
          :dataLoaded="dataLoaded"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
      <v-col cols="12" md="4">
        <StatusHistoryLocationStackedBarChart
          @chartClick="handleChartClick"
          :filterContext="filterStatusHistoryContext"
          :dataLoaded="dataLoaded"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>

    <v-row>        
      <v-col cols="12" md="12">
        <StatusHistoryVelocityLineChart
          @chartClick="handleChartClick"
          :filterContext="filterStatusHistoryContext"
          :dataLoaded="dataLoaded"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>        
    </v-row>

    <v-row>
      <v-col cols="12" md="12">
        <StatusHistoryStatusBurndownLineChart
          @chartClick="handleChartClick"
          :dataLoaded="dataLoaded"
          :filterContext="filterStatusHistoryContext"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>

    <!-- Items Data Table -->
    <v-row>
      <v-col cols="12" md="12">
        <v-skeleton-loader
          v-if="loading"
          type="table"
          height="400"
        ></v-skeleton-loader>
        <ItemsGrid
          v-else
          @chartClick="handleChartClick"
          :filterContext="filterStatusHistoryContext"
          itemsView="filteredItemsByStatusHistory"
          @updateFilter="(chartId, ids) => filterContext.updateFilter(chartId, ids)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useTheme } from 'vuetify';
import StatusHistoryCategoryStackedBarChart from './charts/StatusHistoryCategoryStackedBarChart.vue';
import StatusHistoryTeamsStackedBarChart from './charts/StatusHistoryTeamsStackedBarChart';
import StatusHistoryLocationStackedBarChart from './charts/StatusHistoryLocationStackedBarChart';
import StatusHistoryVelocityLineChart from './charts/StatusHistoryVelocityLineChart.vue';
import StatusHistoryStatusBurndownLineChart from './charts/StatusHistoryStatusBurndownLineChart.vue';
import ItemsGrid from './grids/ItemsGrid.vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

const store = useStore();
const theme = useTheme();
const settings = computed(() => store.state.settings);
const locale = computed(() => store.getters.currentLocale);

const duckDBService = store.getters.getDuckDBService;
const conn = store.getters.getDBConnection;
const dataLoaded = ref(false);

const themeMode = computed(() => {
  return theme.global.name.value === 'dark' ? true : false;
 });


const statusHistory = ref([]);
const timestampCache = ref(new Map());

const filterStatusHistoryContext = computed(() => store.state.filterStatusHistoryContext);

const projectActivityStartDate = ref(null);
const startDate = ref('');
const endDate = ref('');
const dateRange = ref([]);
const minDate = ref(new Date());
const maxDate = ref(new Date());
const showNoActivityWarning = ref(false);

// Create a composable for data fetching state
const useDataFetching = () => {
  const loading = ref(true);
  const progressValue = ref(0);
  let progressInterval = null;

  const startProgress = () => {
    clearInterval(progressInterval);
    progressValue.value = 0;
    progressInterval = setInterval(() => {
      if (progressValue.value < 70) {
        progressValue.value += 10;
      }
    }, 500);
  };

  const stopProgress = () => {
    clearInterval(progressInterval);
    progressValue.value = 100;
  };

  onUnmounted(() => {
    clearInterval(progressInterval);
  });

  return {
    loading,
    progressValue,
    startProgress,
    stopProgress
  };
};

const { loading, progressValue, startProgress, stopProgress } = useDataFetching();



const getInitialDateFrom = () => {
  if (projectActivityStartDate.value) {
    return projectActivityStartDate.value.toISOString().split('T')[0];
  }
  const date = new Date();
  date.setDate(date.getDate() - 60);
  return date.toISOString().split('T')[0];
};


const handleDateRangeCleared = async () => {
  // Clear the dateRange value
  dateRange.value = [];

  // Remove the date range filter to show all data
  if (filterStatusHistoryContext.value) {
    await filterStatusHistoryContext.value.removeFilter('activityDatePicker');
  }
};

const handleDateRangeChange = async (newRange) => {
  // If the range is cleared (null or empty), handle it
  if (!newRange || !Array.isArray(newRange) || newRange.length === 0) {
    await handleDateRangeCleared();
    return;
  }

  if (newRange.length !== 2) {
    return;
  }

  const newStart = toDate(newRange[0]);
  const newEnd = toDate(newRange[1]);

  if (!newStart || !newEnd) {
    return;
  }

  // Update dateRange immediately
  dateRange.value = newRange;

  // Format dates for SQL query (YYYY-MM-DD format)
  const formattedStartDate = formatDateForSQL(newStart);
  const formattedEndDate = formatDateForSQL(newEnd);

  // Generate SQL query for filtering - only include status records where status CHANGED during the date range
  const query = `
    SELECT DISTINCT sh.subItemId, sh.validFrom, sh.statusId
    FROM status_history_temporal sh
    WHERE sh.validFrom >= '${formattedStartDate}'
    AND sh.validFrom <= '${formattedEndDate}'
  `;

  // Pass the query to the filter context
  filterStatusHistoryContext.value.updateFilterWithQuery('activityDatePicker', query);
};

// Helper function to format date for SQL query
const formatDateForSQL = (date) => {
  const validDate = toDate(date);
  if (!validDate) return '';
  
  const year = validDate.getFullYear();
  const month = String(validDate.getMonth() + 1).padStart(2, '0');
  const day = String(validDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  return null;
};

// Check if status_history table exists in the database
const checkStatusHistoryExists = async () => {
  try {
    // Query to check if the table exists in DuckDB
    const result = await conn.query(`
      SELECT table_name 
      FROM information_schema.tables 
      WHERE table_name = 'status_history'
    `);

    const rows = result.toArray()
    
    // If we have rows in the result, the table exists
    return rows && rows.length > 0;
  } catch (error) {
    console.error("Error checking if status_history exists:", error);
    return false;
  }
};

const getData = async () => {
  if (!settings.value.projectCode) return;
  
  try {
    loading.value = true;
    dataLoaded.value = false;
    startProgress();

    // Check if status_history already exists
    const statusHistoryExists = await checkStatusHistoryExists();
    
    if (!statusHistoryExists) {
      const formattedPath = `/api/${settings.value.projectCode}/reporting/items/status-history-short`;
      await duckDBService.downloadAndInsertParquet(formattedPath, 'status_history');
    }

    await conn.loadData(`
      DROP TABLE IF EXISTS status_history_temporal;

      CREATE TABLE status_history_temporal AS
      WITH ordered AS (
        SELECT
            sht.subItemId,
            sht.statusId,
            i.locationId,
            i.createdByTeamId,
            i.createdByUserId,
            i.profileId,
            i.SubItemTemplateId                  AS subItemTemplateId,
            sht.createdDate                      AS validFrom,
            LEAD(sht.createdDate)
              OVER (PARTITION BY sht.subItemId ORDER BY sht.createdDate)
                                                  AS validTo
        FROM status_history sht
        JOIN items i USING (subItemId)             -- add the three columns
      )
      SELECT
          subItemId as subItemId,
          statusId as statusId,
          locationId as locationId,
          createdByTeamId as createdByTeamId,
          createdByUserId as createdByUserId,
          profileId as profileId,
          subItemTemplateId as subItemTemplateId,
          validFrom,
          validTo,
          CASE
              WHEN validTo IS NOT NULL
              THEN DATEDIFF('minute', validFrom, validTo)
              ELSE DATEDIFF('minute', validFrom,
                            CAST(CURRENT_TIMESTAMP AS TIMESTAMP))
          END AS durationMinutes
      FROM ordered
      ORDER BY subItemId, validFrom;   -- keeps IDs highly compressible
    `);

    // Query to find min and max dates in the status_history table
    const dateRangeResult = await conn.query(`
      SELECT 
        MIN(createdDate) as minDate,
        MAX(createdDate) as maxDate
      FROM status_history
    `);
    
    const dateRangeData = dateRangeResult.toArray();
    
    if (dateRangeData && dateRangeData.length > 0) {
      // Normalize to day boundaries: the picker validates selections against
      // min/max including time-of-day. The range always extends to the end of
      // today regardless of when the last status record was created
      minDate.value = new Date(dateRangeData[0].minDate);
      minDate.value.setHours(0, 0, 0, 0);
      maxDate.value = new Date();
      maxDate.value.setHours(23, 59, 59, 999);

      // If dateRange is not set, initialize it with last 61 days up to today
      if (!dateRange.value || dateRange.value.length !== 2) {
        const today = new Date();
        const last61Days = new Date(today);
        last61Days.setDate(last61Days.getDate() - 61);

        // Use the later of minDate or 61 days ago
        const defaultStartDate = last61Days > minDate.value ? last61Days : minDate.value;

        dateRange.value = [defaultStartDate, today];
        startDate.value = formatDateForPicker(defaultStartDate);
        endDate.value = formatDateForPicker(today);
      }
    }

    await store.state.filterStatusHistoryContext.initializeFilterContext();

    // Apply the initial date range filter
    if (dateRange.value && dateRange.value.length === 2) {
      await handleDateRangeChange(dateRange.value);
    }

  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    dataLoaded.value = true;
    stopProgress();
    requestAnimationFrame(() => {
        loading.value = false;      
    });
  }
};

const getDateFormat = (l) => {
  switch (l) {
    case 'en':
      return 'MM/dd/yyyy';
    case 'en-GB':
    case 'en-AU':
      return 'dd/MM/yyyy';
    case 'fr':
      return 'dd/MM/yyyy';
    case 'it':
      return 'dd/MM/yyyy';
    case 'zh-Hans':
    case 'zh-Hant':
      return 'yyyy/MM/dd';
    case 'nl':
      return 'dd-MM-yyyy';
    case 'pt-BR':
      return 'dd/MM/yyyy';
    default:
      return 'dd/MM/yyyy';
  }
};

const formatDateForPicker = (date) => {
  const validDate = toDate(date);
  if (!validDate) return '';
  
  const year = validDate.getFullYear();
  const month = String(validDate.getMonth() + 1).padStart(2, '0');
  const day = String(validDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const stopWatchers = [
  watch(
    () => [settings.value.scopedToken, settings.value.projectCode],
    ([newScopedToken, newProjectCode], oldValue) => {
      if (newScopedToken && newProjectCode && 
          JSON.stringify([newScopedToken, newProjectCode]) !== JSON.stringify(oldValue)) {
        getData();
      }
    },
    { deep: true }
  ),
];

// Initialize data on component mount only if necessary
const initializeData = async () => {
  if (settings.value.scopedToken && settings.value.projectCode) {
    // First check if the status_history table already exists
    const statusHistoryExists = await checkStatusHistoryExists();
    
    if (!statusHistoryExists) {
      // Only fetch data if the table doesn't exist
      await getData();

    } else {
      // If data already exists, initialize filter context and apply date range before marking as loaded
      loading.value = false;

      await store.commit('resetStatusHistoryFilters');

      // Still need to query for min and max dates even if the table exists
      try {
        const dateRangeResult = await conn.query(`
          SELECT
            MIN(createdDate) as minDate,
            MAX(createdDate) as maxDate
          FROM status_history
        `);

        const dateRangeData = dateRangeResult.toArray();

        if (dateRangeData && dateRangeData.length > 0) {
          // Normalize to day boundaries like getData() does: the picker
          // validates selections against min/max including time-of-day. The
          // range always extends to the end of today regardless of when the
          // last status record was created
          minDate.value = new Date(dateRangeData[0].minDate);
          minDate.value.setHours(0, 0, 0, 0);
          maxDate.value = new Date();
          maxDate.value.setHours(23, 59, 59, 999);

          // Set initial date range to last 61 days up to today if not already set
          if (!dateRange.value || dateRange.value.length !== 2) {
            const today = new Date();
            const last61Days = new Date(today);
            last61Days.setDate(last61Days.getDate() - 61);

            // Use the later of minDate or 61 days ago
            const defaultStartDate = last61Days > minDate.value ? last61Days : minDate.value;

            dateRange.value = [defaultStartDate, today];
            startDate.value = formatDateForPicker(defaultStartDate);
            endDate.value = formatDateForPicker(today);

            // Apply the initial date range filter BEFORE marking data as loaded
            await handleDateRangeChange(dateRange.value);
          }
        }
      } catch (error) {
        console.error("Error fetching date range:", error);
      }

      // Mark as loaded AFTER applying the date range filter
      dataLoaded.value = true;
    }
  }
};

onMounted(() => {
  initializeData();
});

// Cleanup watchers and cache when component is unmounted
onUnmounted(() => {
  try {
    stopWatchers.forEach(unwatch => unwatch());
    timestampCache.value.clear();

    // Reset all filters so they don't persist to other pages
    if (filterStatusHistoryContext.value) {
      filterStatusHistoryContext.value.resetFilters().catch(err => {
        console.error('Error resetting filters on unmount:', err);
      });
    }
  } catch (error) {
    console.error('Error in onUnmounted:', error);
  }
});
</script>

<style scoped>
.activity-page-container {
  max-width: 100%;
  /* clip (not hidden): hidden forces overflow-y to auto, creating a nested
     vertical scroll container inside the page scroller */
  overflow-x: clip;
}

@media (max-width: 600px) {
  .activity-page-container {
    max-width: 100vw;
  }
}
</style>

<style>
.white-slider {
  margin-top:20px;
}

.white-slider :deep(.v-slider-track__background) {
  background-color: rgba(255, 255, 255, 0.3) !important;
}

.white-slider :deep(.v-slider-thumb::before) {
  background-color: white !important;
}

.white-slider :deep(.v-slider-thumb__label) {
  background-color: #2d4a5d !important;
  color: white !important;
}

.custom-dialog {
  backdrop-filter: blur(4px);
}

.dp__theme_dark {
    --dp-background-color: #263e4e;
    --dp-text-color: #fff;
    --dp-hover-color: #263e4e;
    --dp-hover-text-color: #fff;
    --dp-hover-icon-color: #2196F3;
    --dp-primary-color: #2196F3;
    --dp-primary-disabled-color: #1a292f;
    --dp-primary-text-color: #fff;
    --dp-secondary-color: #000000;
    --dp-border-color: #1a292f;
    --dp-menu-border-color: #1a292f;
    --dp-border-color-hover: #263e4e;
    --dp-border-color-focus: #2196F3;
    --dp-disabled-color: #1b1b1b;
    --dp-disabled-color-text: #202020;
    --dp-scroll-bar-background: #213542;
    --dp-scroll-bar-color: #263e4e;
    --dp-success-color: #4CAF50;
    --dp-success-color-disabled: #1a292f;
    --dp-icon-color: #fff;
    --dp-danger-color: #FF5252;
    --dp-marker-color: #FF5252;
    --dp-tooltip-color: #263e4e;
    --dp-highlight-color: rgb(33 149 243 / 20%);
    --dp-range-between-dates-background-color: #263e4e;
    --dp-range-between-dates-text-color: #fff;
    --dp-range-between-border-color: #2196F3;
}
</style>